import clsx from "clsx";
import { SectionComponent, useSection } from "@tree-company/tree-query";
import { calculateResults } from "../helpers/helpers";
import { useLayoutEffect, useMemo } from "react";
import Markdown from "react-markdown";
import PROFILES from "../data/profiles";
import translate from "../locales";

export const ActionPlan = ({ page, handleSave }) => {
  const profile = useMemo(
    () => PROFILES[calculateResults(page.treesponse)],
    [],
  );

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="result-container">
      <ResultHeader profile={profile} />
      {page.sections.map((section) => (
        <SectionComponent
          section={section}
          key={section.settings.renderKey || section.key}
          className="page__section"
        />
      ))}
      <button
        className="button button--center result__button"
        onClick={(e) => {
          page.treesponse.completed = true;
          handleSave(e);
        }}
      >
        {translate("profiles.submit")}
      </button>
    </div>
  );
};

export function ResultHeader({ profile }) {
  return (
    <>
      <div className="result">
        <img
          className="result__image"
          src={profile.image}
          alt={translate(`profiles.images.${profile.id}`)}
        />
        <div className="result__content">
          <h1 className="result__header">
            <Markdown>{profile.title}</Markdown>
          </h1>
          <Markdown>{profile.description}</Markdown>
        </div>
      </div>
      <div className="plan-header">
        <h2 className="plan-header__title">{profile.plan.title}</h2>
        <p className="plan-header__text">{profile.plan.description}</p>
      </div>
    </>
  );
}

export function ChooseActionQuestion({ question, handleChange }) {
  const {
    value,
    title,
    key,
    required,
    hint,
    errorMessages,
    valid,
    skip,
    options,
    disabled,
    ariaDisabled,
    htmlAttributes,
  } = useSection(question);
  const renderKey = question.renderKey ?? key;

  function onOptionSelect(event, optionValue) {
    if (ariaDisabled) return;

    if (handleChange) {
      handleChange(event, optionValue);
    } else {
      question.value = optionValue;
      question.validate();
    }
  }

  if (skip) return null;

  return (
    <fieldset
      aria-labelledby={`${renderKey}-label`}
      className={clsx(
        "question",
        "question--radio",
        htmlAttributes.class,
        { "question--required": required },
        { "question--invalid": !valid },
        { "question--disabled": disabled || ariaDisabled },
      )}
      name={key}
      disabled={disabled}
      aria-disabled={ariaDisabled}
    >
      <div className="question__plan-options">
        <h3
          id={`${renderKey}-label`}
          className="question__label question__label--plan"
        >
          <Markdown>{title}</Markdown>
        </h3>
        <div className="question__options question__options--plan">
          {options.map((option, index) => {
            const optionId = [renderKey, option.value].join("-");
            const checked = value === option.value;
            return (
              <div
                key={`${renderKey}-${option.value}-${index}`}
                className="question__option question__option--plan option"
              >
                <input
                  className="sr-only option__input option__input--plan"
                  type="radio"
                  id={optionId}
                  name={key}
                  value={option.value}
                  checked={checked}
                  onChange={(event) => onOptionSelect(event, option.value)}
                  disabled={option.disabled}
                  aria-disabled={option.ariaDisabled}
                />
                <label
                  className="option__label option__label--plan"
                  htmlFor={optionId}
                >
                  <Markdown>{option.label}</Markdown>
                </label>
              </div>
            );
          })}
        </div>
        {errorMessages.length > 0 && (
          <span className="question__error">{errorMessages[0]}</span>
        )}
      </div>
      {hint && <span className="question__hint">{hint}</span>}
    </fieldset>
  );
}
